import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react"
import { X } from "lucide-react"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { fixedPosInPortal, portalRootFor } from "@/components/ui/dropdown-menu-position"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import { MentionList, type MentionListHandle } from "./mention-list"
import { MentionThumb } from "./mention-thumb"
import { MENTION_PREVIEW_OPEN_DELAY_MS, placeMentionThumbPreview } from "./mention-thumb-preview"
import { placeMenuBelowAnchor } from "./place-menu"
import {
  isMentionAutocompleteCategory,
  mentionDisplayLabel,
  mentionKey,
  type MentionCategory,
  type MentionItem,
} from "./types"

export type PromptMentionExtensionOptions = {
  getMentionItems?: () => MentionItem[]
  getOnMentionInsert?: () => ((item: MentionItem) => void) | undefined
  getOnMentionOpen?: () => ((item: MentionItem) => void) | undefined
  getMentionLabel?: () =>
    | ((category: MentionCategory, id: number, stateId?: number | null) => string | undefined)
    | undefined
  getMentionDisplay?: () =>
    | ((category: MentionCategory, id: number, label: string, stateId?: number | null) => MentionChipDisplay | null)
    | undefined
  getReplaceable?: () => boolean
}

export type MentionChipDisplay = {
  text: string
  missing: boolean
  imageUrl?: string | null
  frontViewChrome?: boolean
}

const REPLACEABLE_CATEGORIES = new Set<MentionCategory>(["hero", "loc", "prop", "reference"])

function findMentionItem(
  items: MentionItem[],
  category: MentionCategory,
  id: number,
  stateId: number | null,
  attrKey: string | null,
): MentionItem | undefined {
  const key = mentionKey(category, id, stateId, attrKey)
  return items.find((it) => mentionKey(it.category, it.id, it.stateId, it.attrKey) === key)
}

function resolveImageUrl(
  items: MentionItem[],
  category: MentionCategory,
  id: number,
  stateId: number | null,
  attrKey: string | null,
): string | null {
  return findMentionItem(items, category, id, stateId, attrKey)?.imageUrl ?? null
}

function resolveMediaType(
  items: MentionItem[],
  category: MentionCategory,
  id: number,
  stateId: number | null,
  attrKey: string | null,
): MentionItem["mediaType"] | undefined {
  return findMentionItem(items, category, id, stateId, attrKey)?.mediaType
}

function resolveFrontViewChrome(
  items: MentionItem[],
  category: MentionCategory,
  id: number,
  stateId: number | null,
  attrKey: string | null,
): boolean {
  return findMentionItem(items, category, id, stateId, attrKey)?.frontViewChrome === true
}

const RAW_CAM_LABEL_RE = /^@cam\[\d+\]$/

/** Если в attrs остался сырой @cam[id], подтянуть имя из mentionItems. Attr — перевод по attrKey. */
function resolveDisplayLabel(
  items: MentionItem[],
  category: MentionCategory,
  id: number,
  label: string,
  stateId: number | null,
  attrKey: string | null,
  translateAttr?: (key: string) => string | null,
): string {
  if (category === "attr" && attrKey) {
    const translated = translateAttr?.(attrKey)
    if (translated) return mentionDisplayLabel(category, translated)
  }
  if (category === "cam" && RAW_CAM_LABEL_RE.test(label)) {
    const fromItems = findMentionItem(items, category, id, stateId, attrKey)?.label
    if (fromItems) return mentionDisplayLabel(category, fromItems)
  }
  if (category === "node") {
    const fromItems = findMentionItem(items, category, id, stateId, attrKey)?.label
    if (fromItems) return mentionDisplayLabel(category, fromItems)
  }
  return mentionDisplayLabel(category, label)
}

export function MentionChipView({ node, extension, deleteNode, updateAttributes, editor }: NodeViewProps) {
  const { t } = useTranslation("shot")
  const { t: tWorkspace, i18n } = useTranslation("workspace")
  const options = extension.options as PromptMentionExtensionOptions
  const category = (node.attrs.category ?? "reference") as MentionCategory
  const id = Number(node.attrs.id ?? 0)
  const label = String(node.attrs.label ?? "")
  const stateIdRaw = node.attrs.stateId
  const stateId = stateIdRaw == null || stateIdRaw === "" ? null : Number(stateIdRaw)
  const attrKey = node.attrs.attrKey == null || node.attrs.attrKey === "" ? null : String(node.attrs.attrKey)
  const mentionItems = options.getMentionItems?.() ?? []
  const fileItem = category === "file" ? findMentionItem(mentionItems, category, id, stateId, attrKey) : undefined
  const missingFile = category === "file" && fileItem == null
  const custom = category === "file" ? null : (options.getMentionDisplay?.()?.(category, id, label, stateId) ?? null)
  const display = custom
    ? custom.text
    : missingFile
      ? t("promptEditor.mention.missingFile")
      : resolveDisplayLabel(mentionItems, category, id, fileItem?.label ?? label, stateId, attrKey, (key) => {
          const i18nKey = `entityProfile.sections.${key}`
          if (!i18n.exists(i18nKey, { ns: "workspace" })) return null
          return tWorkspace(i18nKey as never)
        })
  const isCam = category === "cam"
  // Камера: одна общая иконка вместо превью-постера (как у селектора камеры).
  const imageUrl = isCam
    ? null
    : custom?.imageUrl !== undefined
      ? custom.imageUrl
      : resolveImageUrl(mentionItems, category, id, stateId, attrKey)
  const mediaType = isCam ? undefined : resolveMediaType(mentionItems, category, id, stateId, attrKey)
  const mediaIcon =
    mediaType === "audio" ? "audio" : mediaType === "video" ? "video" : mediaType === "file" ? "file" : null
  const hasThumb = Boolean(imageUrl) || isCam || mediaIcon != null
  const frontViewChrome =
    custom?.frontViewChrome ?? resolveFrontViewChrome(mentionItems, category, id, stateId, attrKey)
  const canReplace = REPLACEABLE_CATEGORIES.has(category) && editor.isEditable && options.getReplaceable?.() !== false
  const canOpen = fileItem != null && options.getOnMentionOpen?.() != null
  const pickerItems = mentionItems.filter((it) => isMentionAutocompleteCategory(it.category))

  const chipRef = useRef<HTMLSpanElement | null>(null)
  const menuPanelRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<MentionListHandle | null>(null)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewPos, setPreviewPos] = useState<{ top: number; left: number } | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current != null) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const closePreview = useCallback(() => {
    clearOpenTimer()
    setPreviewOpen(false)
    setPreviewPos(null)
  }, [clearOpenTimer])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setMenuPos(null)
  }, [])

  const onChipEnter = useCallback(() => {
    if (!imageUrl || menuOpen) return
    clearOpenTimer()
    openTimerRef.current = setTimeout(() => setPreviewOpen(true), MENTION_PREVIEW_OPEN_DELAY_MS)
  }, [clearOpenTimer, imageUrl, menuOpen])

  const onChipLeave = useCallback(() => {
    closePreview()
  }, [closePreview])

  useEffect(() => () => clearOpenTimer(), [clearOpenTimer])

  useEffect(() => {
    if (menuOpen) closePreview()
  }, [menuOpen, closePreview])

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (chipRef.current?.contains(target) || menuPanelRef.current?.contains(target)) return
      closeMenu()
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        closeMenu()
        return
      }
      if (listRef.current?.onKeyDown(e)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.addEventListener("mousedown", onDoc)
    document.addEventListener("keydown", onKey, true)
    return () => {
      document.removeEventListener("mousedown", onDoc)
      document.removeEventListener("keydown", onKey, true)
    }
  }, [menuOpen, closeMenu])

  useLayoutEffect(() => {
    if (!previewOpen || !imageUrl || menuOpen) {
      setPreviewPos(null)
      return
    }
    const place = () => {
      const el = chipRef.current
      if (!el) return
      setPreviewPos(fixedPosInPortal(placeMentionThumbPreview(el.getBoundingClientRect()), portalRootFor(el)))
    }
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [previewOpen, imageUrl, menuOpen])

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPos(null)
      return
    }
    const place = () => {
      const chip = chipRef.current
      const panel = menuPanelRef.current
      if (!chip || !panel) return
      setMenuPos(
        fixedPosInPortal(
          placeMenuBelowAnchor(chip.getBoundingClientRect(), panel.getBoundingClientRect()),
          portalRootFor(chip),
        ),
      )
    }
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [menuOpen])

  const onReplace = useCallback(
    (item: MentionItem) => {
      updateAttributes({
        category: item.category,
        id: item.id,
        label: item.label,
        stateId: item.stateId ?? null,
        attrKey: item.attrKey ?? null,
      })
      if (item.category === "hero" || item.category === "loc" || item.category === "prop") {
        options.getOnMentionInsert?.()?.(item)
      }
      closeMenu()
    },
    [updateAttributes, closeMenu, options],
  )

  const openFile = () => {
    const item = findMentionItem(options.getMentionItems?.() ?? [], category, id, stateId, attrKey)
    if (item) options.getOnMentionOpen?.()?.(item)
  }

  const portalRoot = typeof document !== "undefined" ? portalRootFor(chipRef.current) : null

  return (
    <NodeViewWrapper as="span" className="pe-mention-wrap" contentEditable={false}>
      <span
        ref={chipRef}
        className={cn(
          "pe-mention",
          `pe-mention--${category}`,
          frontViewChrome && "pe-mention--prop-front",
          hasThumb && "pe-mention--has-thumb",
          canReplace && "pe-mention--replaceable",
          canOpen && "pe-mention--openable",
          (missingFile || custom?.missing) && "pe-mention--missing",
          menuOpen && "pe-mention--menu-open",
        )}
        data-category={category}
        role={canReplace || canOpen ? "button" : undefined}
        tabIndex={canReplace || canOpen ? 0 : undefined}
        title={canReplace ? t("promptEditor.mention.replace") : canOpen ? t("promptEditor.mention.open") : undefined}
        aria-haspopup={canReplace ? "listbox" : undefined}
        aria-expanded={canReplace ? menuOpen : undefined}
        onMouseEnter={onChipEnter}
        onMouseLeave={onChipLeave}
        onClick={(e) => {
          if (canOpen) {
            e.preventDefault()
            e.stopPropagation()
            closePreview()
            openFile()
            return
          }
          if (!canReplace) return
          e.preventDefault()
          e.stopPropagation()
          closePreview()
          setMenuOpen((v) => !v)
        }}
        onKeyDown={(e) => {
          if (!canReplace && !canOpen) return
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            e.stopPropagation()
            closePreview()
            if (canOpen) openFile()
            else setMenuOpen((v) => !v)
          }
        }}
      >
        {hasThumb ? (
          <span className="pe-mention__thumb-wrap">
            <MentionThumb icon={isCam ? "camera" : mediaIcon} imageUrl={imageUrl!} />
            {editor.isEditable ? (
              <button
                type="button"
                className="pe-mention__remove"
                title={t("promptEditor.mention.remove")}
                aria-label={t("promptEditor.mention.remove")}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  closePreview()
                  closeMenu()
                  deleteNode()
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <X aria-hidden />
              </button>
            ) : null}
          </span>
        ) : null}
        <span className="pe-mention__label">{display}</span>
        {!hasThumb && editor.isEditable ? (
          <button
            type="button"
            className="pe-mention__remove pe-mention__remove--plain"
            title={t("promptEditor.mention.remove")}
            aria-label={t("promptEditor.mention.remove")}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              closePreview()
              closeMenu()
              deleteNode()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <X aria-hidden />
          </button>
        ) : null}
      </span>
      {previewOpen && imageUrl && previewPos && !menuOpen && portalRoot
        ? createPortal(
            <div
              className={cn("pe-mention__preview", frontViewChrome && "pe-mention__preview--prop-front")}
              style={{ top: previewPos.top, left: previewPos.left }}
              aria-hidden
            >
              <img src={imageUrl} alt="" draggable={false} />
            </div>,
            portalRoot,
          )
        : null}
      {menuOpen && portalRoot
        ? createPortal(
            <div
              ref={menuPanelRef}
              className="pe-mention__picker"
              style={
                menuPos
                  ? { top: menuPos.top, left: menuPos.left, visibility: "visible" }
                  : { top: 0, left: 0, visibility: "hidden" }
              }
            >
              <MentionList ref={listRef} items={pickerItems} command={onReplace} />
            </div>,
            portalRoot,
          )
        : null}
    </NodeViewWrapper>
  )
}
